import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
   FaHeadset, FaGlobe, FaFlask, FaHeart, FaCheck
} from "react-icons/fa";
import "./About.css";

function About() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);

  // ✅ Navigate + scroll to top
  const goTo = (path) => {
    navigate(path);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const stats = [
    { number: "50K+", label: "Happy Customers", icon: <FaHeart /> },
    { number: "120+", label: "Countries Served", icon: <FaGlobe /> },
    { number: "500+", label: "Products",         icon: <FaFlask /> },
    { number: "24/7", label: "Support",           icon: <FaHeadset /> },
  ];

  const team = [
    { initials: "RK", name: "Raj Kumar",    role: "Founder & CEO",     bg: "#241e74" },
    { initials: "PS", name: "Priya Sharma", role: "Head of Operations", bg: "#0F6E56" },
    { initials: "AM", name: "Amit Mehta",   role: "Lead Pharmacist",    bg: "#185FA5" },
  ];

  const timeline = [
    { year: "2018", title: "Founded",          desc: "Launched with a mission to make affordable medicines accessible across Europe." },
    { year: "2020", title: "10,000 Customers", desc: "Reached our first major milestone across 40+ countries worldwide." },
    { year: "2022", title: "Global Expansion", desc: "Expanded to 120+ countries with 24/7 multilingual support." },
    { year: "2024", title: "New Platform",     desc: "Redesigned platform with 500+ products and advanced order tracking." },
  ];

  const promises = [
    "Medicines delivered in plain, discreet packaging",
    "100% genuine products from certified manufacturers",
    "Free reshipment if order doesn't arrive",
    "Secure payment with SSL encryption",
    "Real-time order tracking on every shipment",
    "No prescription required for eligible products",
  ];

  const tabs = ["Our Story", "Our Mission", "Our Values"];
  const tabContent = [
    "Founded in 2018, RealGenericShop set out to solve a real problem — making genuine, affordable medicines accessible to people worldwide without stigma or hassle. We started small, serving customers across Europe, and have since grown into a globally trusted online pharmacy serving 50,000+ customers in 120+ countries.",
    "Our mission is simple: make quality healthcare affordable and reachable for everyone, no matter where they are in the world. We believe no one should be denied access to the medicines they need because of location, cost or complexity.",
    "We believe in transparency, quality and care. Every product we sell is verified, every order is packed with discretion, and every customer is treated with respect. These aren't just words — they're the foundation of everything we do.",
  ];

  return (
    <>
      <Helmet>
        <title>About Us | RealGenericShop Online Pharmacy</title>
        <meta name="description" content="Learn about RealGenericShop — trusted online pharmacy delivering genuine GMP certified medicines worldwide." />
          <link rel="canonical" href="https://realgenericshop.com/about" />
      </Helmet>

      <div className="about-page">

        {/* HERO */}
        <section className="ab-hero">
          <div className="container ab-hero-inner">
            <div className="ab-hero-left">
              <h1 className="ab-h1">
                Trusted Care.<br />
                <span className="ab-h1-accent">Worldwide.</span>
              </h1>
              <p className="ab-lead">
                We're an online pharmacy on a mission — delivering genuine,
                GMP-certified medicines to 50,000+ customers across 120 countries
                with discretion, speed and care.
              </p>
              <div className="ab-hero-btns">
                <button className="btn ab-btn-primary" onClick={() => goTo("/")}>
                  Shop Products
                </button>
                <button className="btn ab-btn-outline" onClick={() => goTo("/contact")}>
                  Contact Us
                </button>
              </div>
            </div>
            <div className="ab-hero-right">
              {stats.map((s, i) => (
                <div className="ab-hero-stat" key={i}>
                  <div className="ab-hero-stat-icon">{s.icon}</div>
                  <div className="ab-hero-stat-num">{s.number}</div>
                  <div className="ab-hero-stat-lbl">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* TABS */}
        <section className="ab-tabs-section container">
          <div className="ab-tabs">
            {tabs.map((t, i) => (
              <button
                key={i}
                className={`ab-tab ${activeTab === i ? "active" : ""}`}
                onClick={() => setActiveTab(i)}
              >
                {t}
              </button>
            ))}
          </div>
          <div className="ab-tab-content">
            <p>{tabContent[activeTab]}</p>
          </div>
        </section>

        {/* PROMISE */}
        <section className="ab-promise-section">
          <div className="container ab-promise-inner">
            <div className="ab-promise-left">
              <span className="ab-section-tag">Our Promise</span>
              <h2 className="ab-section-h2">What You Can<br />Always Expect</h2>
              <div className="ab-promise-list">
                {promises.map((p, i) => (
                  <div className="ab-promise-item" key={i}>
                    <div className="ab-check"><FaCheck /></div>
                    <span>{p}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="ab-promise-right">
              <div className="ab-visual-card">
                <div className="ab-vc-top">
                  <div className="ab-vc-pill">Est. 2018</div>
                  <div className="ab-vc-pill">Europe's Trusted Pharmacy</div>
                </div>
                <div className="ab-vc-big">50K+</div>
                <div className="ab-vc-sub">customers trust us worldwide</div>
                <div className="ab-vc-divider"></div>
                <div className="ab-vc-row">
                  <div><div className="ab-vc-n">120+</div><div className="ab-vc-l">Countries</div></div>
                  <div><div className="ab-vc-n">500+</div><div className="ab-vc-l">Products</div></div>
                  <div><div className="ab-vc-n">99%</div><div className="ab-vc-l">Satisfaction</div></div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* TIMELINE */}
        <section className="ab-timeline-section">
          <div className="container">
            <div className="ab-section-header">
              <span className="ab-section-tag">Our Journey</span>
              <h2 className="ab-section-h2">From Idea to Global Pharmacy</h2>
            </div>
            <div className="ab-timeline-row">
              {timeline.map((t, i) => (
                <div className="ab-tl-card" key={i}>
                  <div className="ab-tl-year">{t.year}</div>
                  <div className="ab-tl-bar"></div>
                  <h4>{t.title}</h4>
                  <p>{t.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* TEAM */}
        <section className="ab-team-section container">
          <div className="ab-section-header">
            <span className="ab-section-tag">The Team</span>
            <h2 className="ab-section-h2">People Behind the Care</h2>
          </div>
          <div className="ab-team-grid">
            {team.map((m, i) => (
              <div className="ab-team-card" key={i}>
                <div className="ab-avatar" style={{ background: m.bg }}>
                  {m.initials}
                </div>
                <div className="ab-team-info">
                  <h4>{m.name}</h4>
                  <p>{m.role}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="ab-cta-section container">
          <div className="ab-cta-box">
            <div className="ab-cta-left">
              <h2>Ready to Order?</h2>
              <p>Browse 500+ genuine products with fast worldwide delivery.</p>
            </div>
            <div className="ab-cta-right">
              <button className="btn ab-btn-white" onClick={() => goTo("/")}>
                Shop Now
              </button>
              <button className="btn ab-btn-outline-white" onClick={() => goTo("/contact")}>
                Get In Touch
              </button>
            </div>
          </div>
        </section>

      </div>
    </>
  );
}

export default About;